import React from "react";
import { useTranslations } from "next-intl";
import Head from "next/head";
import { WEBPAGE_TITLE } from "@utils/Constants";
import Page from "@components/Layout/page";
import { Leaf } from "@components/Leaf/Leaf";
import { useInView } from "@hooks/useInView";
import styles from "./CompanyEnvironmentalPolicyPage.module.scss";

export default function CompanyEnvironmentalPolicyPage() {
    const t = useTranslations("CompanyEnvironmentalPolicyPage");

    const [heroRef, heroInView] = useInView();
    const [benefitsRef, benefitsInView] = useInView();
    const [galleryRef, galleryInView] = useInView();
    const [processRef, processInView] = useInView();
    const [screenshotRef, screenshotInView] = useInView();
    const [ctaRef, ctaInView] = useInView();

    return (
        <Page fluid bodyClassName={styles.pageBody}>
            <Head>
                <title key="site_title">
                    {"Umhverfisstefna - " + WEBPAGE_TITLE}
                </title>
                <meta
                    name="keywords"
                    content="Umhverfisstefna, sjálfbærni, ESG"
                />
                <meta
                    property="og:title"
                    content={`${WEBPAGE_TITLE} - Umhverfisstefna`}
                    key="og:title"
                />
                <meta
                    name="og:description"
                    content={t("hero_desc")}
                    key="og:description"
                />
                <meta name="description" content={t("hero_desc")} />
            </Head>

            <div className={styles.wrapper}>
                <Leaf className={styles.leafBg1} plain />
                <Leaf className={styles.leafBg2} plain />
                <Leaf className={styles.leafBg3} plain />

                <section
                    ref={heroRef}
                    className={`${styles.hero} ${styles.revealTarget} ${heroInView ? styles.revealed : ""}`}
                >
                    <div className={styles.heroContent}>
                        <span className={styles.badge}>{t("badge")}</span>
                        <h1 className={styles.heroTitle}>{t("hero_title")}</h1>
                        <p className={styles.heroSubtitle}>
                            {t("hero_subtitle")}
                        </p>
                        <p className={styles.heroDesc}>{t("hero_desc")}</p>
                        <a className={styles.ctaBtn} href="/hafa-samband">
                            {t("contact_us")}
                        </a>
                    </div>
                </section>

                <div className={styles.showcase}>
                    <section
                        ref={benefitsRef}
                        className={`${styles.showcaseRow} ${styles.revealTarget} ${benefitsInView ? styles.revealed : ""}`}
                    >
                        <div className={styles.showcaseText}>
                            <span className={styles.showcaseLabel}>
                                {t("showcase_benefits_label")}
                            </span>
                            <h2 className={styles.showcaseTitle}>
                                {t("showcase_benefits_title")}
                            </h2>
                            <p className={styles.showcaseDesc}>
                                {t("showcase_benefits_desc")}
                            </p>
                        </div>
                        <div className={styles.showcaseVisual}>
                            <div className={styles.statGrid}>
                                <div className={styles.statCard}>
                                    <div className={styles.statIcon}>
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <line
                                                x1="12"
                                                y1="1"
                                                x2="12"
                                                y2="23"
                                            />
                                            <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                                        </svg>
                                    </div>
                                    <span className={styles.statLabel}>
                                        {t("stat_cost")}
                                    </span>
                                    <span className={styles.statMeta}>
                                        {t("stat_cost_desc")}
                                    </span>
                                </div>
                                <div className={styles.statCard}>
                                    <div className={styles.statIcon}>
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                                        </svg>
                                    </div>
                                    <span className={styles.statLabel}>
                                        {t("stat_image")}
                                    </span>
                                    <span className={styles.statMeta}>
                                        {t("stat_image_desc")}
                                    </span>
                                </div>
                                <div className={styles.statCard}>
                                    <div className={styles.statIcon}>
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                                        </svg>
                                    </div>
                                    <span className={styles.statLabel}>
                                        {t("stat_compliance")}
                                    </span>
                                    <span className={styles.statMeta}>
                                        {t("stat_compliance_desc")}
                                    </span>
                                </div>
                                <div
                                    className={`${styles.statCard} ${styles.statCardAccent}`}
                                >
                                    <div className={styles.statIcon}>
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                            <polyline points="22 4 12 14.01 9 11.01" />
                                        </svg>
                                    </div>
                                    <span className={styles.statLabel}>
                                        {t("stat_results")}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section
                        ref={galleryRef}
                        className={`${styles.gallery} ${styles.revealTarget} ${galleryInView ? styles.revealed : ""}`}
                    >
                        <span className={styles.showcaseLabel}>
                            {t("gallery_label")}
                        </span>
                        <h2 className={styles.galleryTitle}>
                            {t("gallery_title")}
                        </h2>
                        <div className={styles.galleryGrid}>
                            <img
                                className={styles.galleryImg}
                                src="/img/company_env/company_env_plocy_book5.jpg"
                                alt="Umhverfisstefna"
                            />
                            <img
                                className={styles.galleryImg}
                                src="/img/company_env/company_env_plocy_book1.jpg"
                                alt="Umhverfisstefna"
                            />
                            <img
                                className={styles.galleryImg}
                                src="/img/company_env/company_env_plocy_book2.jpg"
                                alt="Umhverfisstefna"
                            />
                            <img
                                className={styles.galleryImg}
                                src="/img/company_env/company_env_plocy_book3.jpg"
                                alt="Umhverfisstefna"
                            />
                            <img
                                className={styles.galleryImg}
                                src="/img/company_env/company_env_plocy_book4.jpg"
                                alt="Umhverfisstefna"
                            />
                        </div>
                    </section>

                    <section
                        ref={processRef}
                        className={`${styles.showcaseRow} ${styles.reversed} ${styles.revealTarget} ${processInView ? styles.revealed : ""}`}
                    >
                        <div className={styles.showcaseText}>
                            <span className={styles.showcaseLabel}>
                                {t("showcase_process_label")}
                            </span>
                            <h2 className={styles.showcaseTitle}>
                                {t("showcase_process_title")}
                            </h2>
                            <p className={styles.showcaseDesc}>
                                {t("showcase_process_desc")}
                            </p>
                        </div>
                        <div className={styles.showcaseVisual}>
                            <div className={styles.processStack}>
                                <div className={styles.processCard}>
                                    <span className={styles.processNumber}>
                                        1
                                    </span>
                                    <span className={styles.processLabel}>
                                        {t("process_step_1")}
                                    </span>
                                </div>
                                <div className={styles.processCard}>
                                    <span className={styles.processNumber}>
                                        2
                                    </span>
                                    <span className={styles.processLabel}>
                                        {t("process_step_2")}
                                    </span>
                                </div>
                                <div className={styles.processCard}>
                                    <span className={styles.processNumber}>
                                        3
                                    </span>
                                    <span className={styles.processLabel}>
                                        {t("process_step_3")}
                                    </span>
                                </div>
                                <div className={styles.processCard}>
                                    <span className={styles.processNumber}>
                                        4
                                    </span>
                                    <span className={styles.processLabel}>
                                        {t("process_step_4")}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>

                <section
                    ref={screenshotRef}
                    className={`${styles.screenshot} ${styles.revealTarget} ${screenshotInView ? styles.revealed : ""}`}
                >
                    <div className={styles.screenshotInner}>
                        <span className={styles.showcaseLabel}>
                            {t("screenshot_label")}
                        </span>
                        <h2 className={styles.screenshotTitle}>
                            {t("screenshot_title")}
                        </h2>
                        <div className={styles.screenshotFrame}>
                            <img
                                className={styles.screenshotImg}
                                src="/img/umhverfisstefnu_img.jpg"
                                alt={t("screenshot_title")}
                            />
                        </div>
                    </div>
                </section>

                <section
                    ref={ctaRef}
                    className={`${styles.bottomCta} ${styles.revealTarget} ${ctaInView ? styles.revealed : ""}`}
                >
                    <div className={styles.bottomCtaContent}>
                        <h2 className={styles.bottomCtaTitle}>
                            {t("bottom_title")}
                        </h2>
                        <p className={styles.bottomCtaDesc}>
                            {t("bottom_desc")}
                        </p>
                        <a className={styles.ctaBtnLight} href="/hafa-samband">
                            {t("contact_us")}
                        </a>
                    </div>
                </section>
            </div>
        </Page>
    );
}
